// Formatting helpers from docs/design/COMPONENTS.md, shared by the pipes and component code.

const trim = (value: number) => String(+value.toFixed(1));

/** 0 → "0 ms"; <1s "x ms"; <60s "x seconde(n)"; <60m "x minuut/minuten"; anders "x uur" */
export function formatMs(value: number | string | null | undefined): string {
    const ms = Number(value ?? 0);
    if (!ms) {
        return '0 ms';
    }
    if (ms < 1000) {
        return `${ms} ms`;
    }
    const seconds = +(ms / 1000).toFixed(1);
    if (seconds < 60) {
        return `${trim(seconds)} ${seconds === 1 ? 'seconde' : 'seconden'}`;
    }
    const minutes = +(seconds / 60).toFixed(1);
    if (minutes < 60) {
        return `${trim(minutes)} ${minutes === 1 ? 'minuut' : 'minuten'}`;
    }
    return `${trim(minutes / 60)} uur`;
}

/** <1000 "x bytes"; <1e6 "x KB"; anders "x MB" (1 decimaal) */
export function formatBytes(value: number | string | null | undefined): string {
    const bytes = Number(value ?? 0);
    if (bytes < 1000) {
        return `${bytes} bytes`;
    }
    if (bytes < 1_000_000) {
        return `${trim(bytes / 1000)} KB`;
    }
    return `${trim(bytes / 1_000_000)} MB`;
}

/** minutes → "h:mm"; negative values get a minus sign */
export function formatDuration(value: number | string | null | undefined): string {
    const total = Math.round(Number(value ?? 0));
    const abs = Math.abs(total);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `${total < 0 ? '-' : ''}${hours}:${String(minutes).padStart(2, '0')}`;
}

/** "Vandaag", "Gisteren", "Morgen", "x dagen geleden" or "over x dagen" */
export function relativeDay(value: Date | string | null | undefined, now = new Date()): string {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    const day = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((day - today) / 86_400_000);
    switch (days) {
        case 0:
            return 'Vandaag';
        case -1:
            return 'Gisteren';
        case 1:
            return 'Morgen';
    }
    return days < 0 ? `${-days} dagen geleden` : `over ${days} dagen`;
}

/** "Pauze 30 min" or "Pauze 3:30 uur" */
export function formatPause(minutes: number): string {
    return minutes < 60 ? `Pauze ${minutes} min` : `Pauze ${formatDuration(minutes)} uur`;
}

export interface TimedEntry {
    start: Date | string;
    end: Date | string;
}

export type EntryOrBreak<T> = { entry: T; breakMinutes?: never } | { entry?: never; breakMinutes: number };

/** Inserts the gaps between consecutive entries (sorted by start) as pause rows. */
export function withBreaks<T>(entries: T[], range: (entry: T) => TimedEntry, minBreak = 1): EntryOrBreak<T>[] {
    const rows: EntryOrBreak<T>[] = [];
    let previousEnd: number = null;
    for (const entry of entries) {
        const {start, end} = range(entry);
        const startMs = new Date(start).getTime();
        if (previousEnd !== null) {
            const gap = Math.round((startMs - previousEnd) / 60_000);
            if (gap >= minBreak) {
                rows.push({breakMinutes: gap});
            }
        }
        rows.push({entry});
        previousEnd = Math.max(previousEnd ?? 0, new Date(end).getTime());
    }
    return rows;
}
